import React, { useEffect, useState } from 'react';

/**
 * ProgressIndicator Component
 * Shows loading state for async operations
 * Auto-dismisses after timeout and has manual close button.
 */
function ProgressIndicator({ isVisible, message = 'Processing...', type = 'default', onClose }) {
    const [showClose, setShowClose] = useState(false);

    // Show close button after 8s so user can dismiss a stuck loader
    useEffect(() => {
        if (!isVisible) {
            setShowClose(false);
            return;
        }
        const timer = setTimeout(() => setShowClose(true), 8000);
        return () => clearTimeout(timer);
    }, [isVisible, message]);

    if (!isVisible) return null;

    // Type-specific styling
    const getTypeConfig = () => {
        switch (type) {
            case 'upload':
                return { icon: '⬆️', color: 'var(--accent)' };
            case 'download':
                return { icon: '⬇️', color: 'var(--accent)' };
            case 'processing':
                return { icon: '⚙️', color: 'var(--warning)' };
            case 'success':
                return { icon: '✅', color: 'var(--success)' };
            case 'error':
                return { icon: '❌', color: 'var(--danger)' };
            default:
                return { icon: '⏳', color: 'var(--accent)' };
        }
    };

    const config = getTypeConfig();

    return (
        <div style={{
            position: 'fixed',
            top: '24px',
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10001,
            pointerEvents: 'none',
        }}>
            <div style={{
                pointerEvents: 'auto',
                background: 'rgba(15, 23, 42, 0.95)',
                border: `1px solid ${config.color}`,
                borderRadius: '8px',
                padding: '16px 24px',
                minWidth: '260px',
                maxWidth: '90vw',
                textAlign: 'center',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.6)',
                backdropFilter: 'blur(8px)',
                animation: 'fadeIn 0.2s ease-in'
            }}>
                {/* Spinner */}
                <div style={{
                    width: '48px',
                    height: '48px',
                    margin: '0 auto 16px',
                    border: `4px solid var(--bgTertiary)`,
                    borderTop: `4px solid ${config.color}`,
                    borderRadius: '50%',
                    animation: type === 'success' || type === 'error' ? 'none' : 'spin 1s linear infinite'
                }}>
                    {(type === 'success' || type === 'error') && (
                        <div style={{
                            fontSize: '32px',
                            lineHeight: '40px'
                        }}>
                            {config.icon}
                        </div>
                    )}
                </div>

                {/* Message */}
                <div style={{
                    color: 'var(--textPrimary)',
                    fontSize: '14px',
                    fontWeight: 500
                }}>
                    {message}
                </div>

                {/* Pulsing dots for active states */}
                {type !== 'success' && type !== 'error' && (
                    <div style={{
                        marginTop: '8px',
                        color: config.color,
                        fontSize: '18px',
                        fontWeight: 'bold',
                        animation: 'pulse 1.5s ease-in-out infinite'
                    }}>
                        •••
                    </div>
                )}

                {/* Dismiss button — available immediately if onClose provided, or after 8s / on finish */}
                {(onClose || showClose || type === 'success' || type === 'error') && (
                    <button
                        onClick={() => onClose && onClose()}
                        style={{
                            marginTop: '14px',
                            padding: '6px 20px',
                            background: 'transparent',
                            color: 'var(--textSecondary)',
                            border: '1px solid var(--borderSecondary)',
                            cursor: 'pointer',
                            fontSize: '11px',
                            fontWeight: 600,
                            textTransform: 'uppercase'
                        }}
                    >
                        Dismiss
                    </button>
                )}

                <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }

          @keyframes fadeIn {
            from { opacity: 0; transform: scale(0.9); }
            to { opacity: 1; transform: scale(1); }
          }

          @keyframes pulse {
            0%, 100% { opacity: 0.3; }
            50% { opacity: 1; }
          }
        `}</style>
            </div>
        </div>
    );
}

export default ProgressIndicator;
